import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2Icon, ClockIcon, EyeIcon, SendIcon, SparklesIcon, ArrowRightIcon, BoxIcon, FileTextIcon } from "lucide-react";
import { BRAND, ELM, MAIN } from "../lib/data";
import { listing as phillyListing } from "../data/reportPhilly";
import { TopBar } from "./TopBar";
import { ClickHint } from "./ClickHint";
type Stage = "Ordered" | "In Process" | "In Review" | "Ready";
type ViewTab = "orders" | "watching" | "questions";
type CustomerOrder = {
  id: string;
  address: string;
  image: string;
  packageName: string;
  stage: Stage;
  detail: string;
  eta: string;
  attention?: boolean;
};
const stages: Stage[] = ["Ordered", "In Process", "In Review", "Ready"];
const orders: CustomerOrder[] = [{
  id: "elm",
  address: ELM.address,
  image: ELM.hero,
  packageName: "Marketing Package",
  stage: "In Review",
  detail: "Creative approval requested",
  eta: "ETA: Thu, 2:00 PM",
  attention: true
}, {
  id: "main",
  address: MAIN.address,
  image: MAIN.hero,
  packageName: "Digital Launch Package",
  stage: "Ordered",
  detail: "Order received",
  eta: "Starting within 24 hours"
}, {
  id: "report",
  address: phillyListing.address,
  image: phillyListing.heroImage,
  packageName: "Digital Ads Report",
  stage: "Ready",
  detail: "Report ready to view",
  eta: "Delivered today"
}, {
  id: "oak",
  address: "88 Oak Avenue",
  image: ELM.photos[2],
  packageName: "Social Ad Package",
  stage: "In Process",
  detail: "Ads in production",
  eta: "ETA: Fri, 10:00 AM"
}];
const tabs: {
  id: ViewTab;
  label: string;
  icon: typeof EyeIcon;
}[] = [{
  id: "orders",
  label: "My Orders",
  icon: ClockIcon
}, {
  id: "watching",
  label: "Watching",
  icon: EyeIcon
}, {
  id: "questions",
  label: "Questions",
  icon: BoxIcon
}];
export function CustomerOrders({
  onHome,
  onTrack,
  onOpenReport
}: {
  onHome: () => void;
  onTrack: () => void;
  onOpenReport: () => void;
}) {
  const [tab, setTab] = useState<ViewTab>("orders");
  const [draft, setDraft] = useState("");
  const visibleOrders = tab === "watching" ? orders.filter(order => order.attention) : tab === "questions" ? orders.filter(order => order.attention) : orders;
  return <div className="min-h-full w-full bg-neutral-50 text-neutral-900" data-id="element-178" data-mp="2tsvpc1062kt6">
      <TopBar onHome={onHome} title="My Orders" data-id="element-179" data-mp="2ys8cynyv3vwt" />
      <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-5 py-6 sm:px-8 lg:flex-row" data-id="element-180" data-mp="2yqx3q8mndvyb">
        <main className="min-w-0 flex-1" data-id="element-181" data-mp="2krntdpl4hw29">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end" data-id="element-182" data-mp="2psl7nr30i5yh">
            <div data-id="element-183" data-mp="2av15erxi2ow0">
              <p className="text-sm font-medium" style={{
              color: BRAND.accentText
            }} data-id="element-184" data-mp="2avkq2gejbrdq">Bob Carter's marketing services</p>
              <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight" data-id="element-185" data-mp="2fvyn73ellgwz">My Orders</h1>
              <p className="mt-1 text-[15px] text-neutral-500" data-id="element-186" data-mp="947jlmwx5bfk">Everything moving your listings forward, in one clear view.</p>
            </div>
            <button onClick={onHome} className="text-sm font-semibold text-neutral-600 transition hover:text-neutral-900" data-id="element-187" data-mp="1yk0fzi3gs2v2">← Back to home</button>
          </div>

          <div className="mt-6 flex items-center gap-1 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-sm" data-id="element-188" data-mp="1tjdtw4u07hk9">
            {tabs.map(({
            id,
            label,
            icon: Icon
          }) => {
            const selected = tab === id;
            return <button key={id} onClick={() => setTab(id)} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${selected ? "text-white shadow-sm" : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"}`} style={selected ? {
              backgroundColor: BRAND.accent
            } : undefined} data-id="element-189" data-mp="2h9q980rkwgkv">
                  <Icon className="h-4 w-4" data-id="element-190" data-mp="wuo9o7uwmcv4" /> {label}
                  {id === "questions" && <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${selected ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"}`} data-id="element-191" data-mp="rw39u9jkdv77">1</span>}
                </button>;
          })}
          </div>

          <div className="mt-5 flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-5 py-3.5 shadow-sm" data-id="element-192" data-mp="yc3fvdrfwml2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white" style={{
            backgroundColor: BRAND.accent
          }} data-id="element-193" data-mp="13b6jctrrukqx"><SparklesIcon className="h-4 w-4" data-id="element-194" data-mp="188gjb3cseet0" /></span>
            <div data-id="element-195" data-mp="ankab5zrynwo"><p className="text-sm font-semibold" data-id="element-196" data-mp="antxkltfqgyo">Live updates</p><p className="text-xs text-neutral-500" data-id="element-197" data-mp="aky171x8k0cc">Your 12 Elm Street creative is ready for review.</p></div>
            <button onClick={onTrack} className="ml-auto hidden items-center gap-1 text-sm font-semibold sm:inline-flex" style={{
            color: BRAND.accentText
          }} data-id="element-198" data-mp="kkykwoz0oih2">Review now <ArrowRightIcon className="h-4 w-4" data-id="element-199" data-mp="pdrtjnxlwayf" /></button>
          </div>

          {tab === "questions" && <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-id="element-200" data-mp="3nzkvzbeoh6um"><div className="mt-0.5 h-5 w-5 shrink-0" data-id="element-201" data-mp="2a5nk3b2i1y6r" /><p data-id="element-202" data-mp="2a4m7311xb1me"><strong data-id="element-203" data-mp="2a4cjtl89j8ke">One item needs your input.</strong> Please approve the final creative direction for 12 Elm Street to keep your Thursday launch on schedule.</p></div>}

          <div className="mt-6 overflow-x-auto pb-4" data-id="element-204" data-mp="214g07y0qczw7">
            <div className="grid min-w-[950px] grid-cols-4 gap-4" data-id="element-205" data-mp="2134o9ax4slu0">
              {stages.map(stage => {
              const stageOrders = visibleOrders.filter(order => order.stage === stage);
              return <StageLane key={stage} stage={stage} orders={stageOrders} onTrack={onTrack} onOpenReport={onOpenReport} data-id="element-206" data-mp="1r82zwhaojnsh" />;
            })}
            </div>
          </div>
        </main>
        <CustomerChat draft={draft} setDraft={setDraft} data-id="element-207" data-mp="1iezlo6s7n6ws" />
      </div>
    </div>;
}
function StageLane({
  stage,
  orders: stageOrders,
  onTrack,
  onOpenReport
}: {
  stage: Stage;
  orders: CustomerOrder[];
  onTrack: () => void;
  onOpenReport: () => void;
}) {
  return <section className="min-h-[420px] rounded-2xl border border-neutral-200 bg-neutral-100/70 p-3" data-id="element-208" data-mp="13gxg5r1mcbcw">
      <div className="flex items-center justify-between px-1 pb-3" data-id="element-209" data-mp="ttbofheqeii6">
        <h2 className="text-sm font-bold text-neutral-800" data-id="element-210" data-mp="ysod87p5xzne">{stage}</h2>
        <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-1.5 text-xs font-semibold text-neutral-500" data-id="element-211" data-mp="ytzgm6ep55dz">{stageOrders.length}</span>
      </div>
      <div className="space-y-3" data-id="element-212" data-mp="ov9xkab2dew5">
        {stageOrders.map(order => <CustomerOrderCard key={order.id} order={order} onTrack={onTrack} onOpenReport={onOpenReport} data-id="element-213" data-mp="1ckcoy9vi2m3u" />)}
        {!stageOrders.length && <div className="rounded-xl border border-dashed border-neutral-300 bg-white/40 px-4 py-8 text-center text-xs text-neutral-400" data-id="element-214" data-mp="1hi4gv3w5a881">No orders here yet</div>}
      </div>
    </section>;
}
function CustomerOrderCard({
  order,
  onTrack,
  onOpenReport
}: {
  order: CustomerOrder;
  onTrack: () => void;
  onOpenReport: () => void;
}) {
  const isReport = order.id === "report";
  const body = <div className={`rounded-xl border bg-white p-3 shadow-sm transition ${order.attention ? "border-transparent ring-2" : "border-neutral-200"}`} style={order.attention ? {
    boxShadow: `0 0 0 2px ${BRAND.accent}`
  } : undefined} data-id="element-215" data-mp="209xa8zf4lvga">
    <img src={order.image} alt={order.address} className="h-28 w-full rounded-lg object-cover" data-id="element-216" data-mp="3g0pojsvh90rq" />
    <div className="mt-3" data-id="element-217" data-mp="3up414vip43lh"><p className="text-sm font-bold text-neutral-900" data-id="element-218" data-mp="3urq8ov5l6j3p">{order.address}</p><p className="mt-0.5 text-xs text-neutral-500" data-id="element-219" data-mp="3praww80ydjnu">{order.packageName}</p></div>
    <span className={`mt-3 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold ${order.attention ? "" : isReport ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`} style={order.attention ? {
      backgroundColor: BRAND.accentSoft,
      color: BRAND.accentText
    } : undefined} data-id="element-220" data-mp="3229j059cpgb6">{order.attention ? <ClockIcon className="h-3 w-3" data-id="element-221" data-mp="372e1g0ttkl41" /> : isReport ? <FileTextIcon className="h-3 w-3" data-id="element-222" data-mp="r8nevmhkf1h1" /> : <CheckCircle2Icon className="h-3 w-3" data-id="element-223" data-mp="r7lu2qaqz0m4" />}{order.detail}</span>
    <p className="mt-2 text-[11px] font-medium text-neutral-400" data-id="element-224" data-mp="mavd80r4c0zn">{order.eta}</p>
    {order.id === "elm" && <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold" style={{
      color: BRAND.accentText
    }} data-id="element-225" data-mp="2ftknj9c9e0cr">Track this order <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-226" data-mp="2ks5mz6josg05" /></span>}
    {isReport && <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold" style={{
      color: BRAND.accentText
    }} data-id="element-227" data-mp="2ppxpeuhyjkil">View marketing report <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-228" data-mp="2ujs57mufzfce" /></span>}
  </div>;
  if (order.id === "elm") return <ClickHint onClick={onTrack} label="Track" rounded="rounded-xl" className="w-full" data-id="element-229" data-mp="2zgiloba5scyc">{body}</ClickHint>;
  if (isReport) return <ClickHint onClick={onOpenReport} label="Open report" rounded="rounded-xl" className="w-full" data-id="element-230" data-mp="38hgr8y08wsj1">{body}</ClickHint>;
  return body;
}
function CustomerChat({
  draft,
  setDraft
}: {
  draft: string;
  setDraft: (draft: string) => void;
}) {
  const chips = ["Approve the 42 Willow Court order", "Send me additional order info", "Request a call"];
  return <aside className="lg:w-[330px]" data-id="element-231" data-mp="1j3bnfmvf0mmd"><div className="flex h-[calc(100vh-7rem)] min-h-[560px] flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white lg:sticky lg:top-[5.5rem]" data-id="element-232" data-mp="1j4mqtlky7scy">
    <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-4" data-id="element-233" data-mp="16r9hht1lglwb"><span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{
          backgroundColor: BRAND.accent
        }} data-id="element-234" data-mp="11soity1zqa9z"><SparklesIcon className="h-4 w-4" data-id="element-235" data-mp="wvo8vc20sndl" /></span><div data-id="element-236" data-mp="1uhlon1p0melu"><p className="text-sm font-semibold" data-id="element-237" data-mp="1uhlngyda48k9">Order concierge</p><p className="text-xs text-neutral-500" data-id="element-238" data-mp="1uh3uqbptsch3">Questions, approvals, and support.</p></div></div>
    <div className="flex-1 space-y-4 px-5 py-5" data-id="element-239" data-mp="1pjbv0vixvm2c"><div className="rounded-2xl rounded-bl-md bg-neutral-100 px-3.5 py-3 text-sm text-neutral-800" data-id="element-240" data-mp="1kjfmcizar6gg">Your 12 Elm Street creative is ready to review. I can help you approve it or answer questions.</div><button onClick={() => setDraft("Request a concierge call")} className="flex w-full items-center justify-between rounded-xl border border-neutral-200 bg-white p-3 text-left shadow-sm transition hover:border-neutral-300" data-id="element-241" data-mp="94iwx7z7af8k"><span data-id="element-242" data-mp="3l6fx1gas3ap0"><span className="block text-sm font-semibold" data-id="element-243" data-mp="3l67k5oeno5dt">Need a person?</span><span className="block text-xs text-neutral-500" data-id="element-244" data-mp="3g7w95acmjou0">Request a concierge call</span></span><ArrowRightIcon className="h-4 w-4" style={{
            color: BRAND.accentText
          }} data-id="element-245" data-mp="3g5tbq5956tf9" /></button></div>
    <div className="space-y-2 border-t border-neutral-100 px-4 pt-3" data-id="element-246" data-mp="3q629hnis8mwr">{chips.map(chip => <motion.button key={chip} onClick={() => setDraft(chip)} whileTap={{
          scale: 0.97
        }} className="flex w-full items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-left text-[13px] font-medium text-neutral-700 transition hover:bg-white" data-id="element-247" data-mp="3q3zawf3kdlgf"><SparklesIcon className="h-3.5 w-3.5 shrink-0" style={{
            color: BRAND.accentText
          }} data-id="element-248" data-mp="hph8ou0b104q" /><span className="truncate" data-id="element-249" data-mp="cryuao3ipspc">{chip}</span></motion.button>)}</div>
    <div className="p-4" data-id="element-250" data-mp="2jiol9voj93mx"><div className="flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2" data-id="element-251" data-mp="2ji5cp5krvrlk"><input value={draft} onChange={event => setDraft(event.target.value)} placeholder="Message your concierge…" className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400" data-id="element-252" data-mp="2eju3mx2axl4e" /><button className="flex h-8 w-8 items-center justify-center rounded-lg text-white" style={{
            backgroundColor: BRAND.accent
          }} aria-label="Send message" data-id="element-253" data-mp="yy9sd4igjkd1"><SendIcon className="h-4 w-4" data-id="element-254" data-mp="p8zxjgw8bnl8" /></button></div></div>
  </div></aside>;
}